import { memo, useCallback, useState, useRef, useEffect } from "react";
import { Modal } from "./Modal";
import { Button, ButtonVariant } from "./Form/Button";
import { ReactComponent as HeightIcon } from "../assets/height.svg";
import { Link } from "./Link";
import { Select } from "./Form/Select";
import { Field } from "./Form/Field";

interface ConditionalDataModalProps {
  isOpen: boolean;
  requiredField: string;
  onSubmit: (data: Record<string, any>) => void;
  onCancel: () => void;
  loading?: boolean;
}

// iOS-style scrollable picker component
const ScrollablePicker = memo(function ScrollablePicker<
  T extends string | number
>({
  values,
  selectedValue,
  onValueChange,
  unit,
  disabled = false,
}: {
  values: T[];
  selectedValue: T;
  onValueChange: (value: T) => void;
  unit: string;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemHeight = 48;

  useEffect(() => {
    if (containerRef.current) {
      const index = values.indexOf(selectedValue);
      containerRef.current.scrollTop = index * itemHeight;
    }
  }, [selectedValue, values]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const index = Math.round(containerRef.current.scrollTop / itemHeight);
    const value = values[Math.max(0, Math.min(index, values.length - 1))];
    if (value !== selectedValue) onValueChange(value);
  };

  return (
    <div className="relative h-60">
      <div className="absolute inset-x-0 top-24 h-12 bg-3461fd/10 border-y-2 border-3461fd/20 pointer-events-none z-10" />
      <div
        ref={containerRef}
        className="h-full overflow-y-scroll scrollbar-hide"
        onScroll={handleScroll}
        style={{ scrollSnapType: "y mandatory" }}
      >
        <div className="h-24" />
        {values.map((value) => (
          <div
            key={value}
            className={`h-12 flex items-center justify-center text-18 font-medium transition-all ${
              value === selectedValue ? "text-3461fd scale-110" : "text-858494"
            }`}
            style={{ scrollSnapAlign: "start" }}
          >
            {value} {unit}
          </div>
        ))}
        <div className="h-24" />
      </div>
      <div className="absolute top-0 inset-x-0 h-12 bg-gradient-to-b from-white to-transparent pointer-events-none" />
      <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </div>
  );
});

export const ConditionalDataModal = memo(function ConditionalDataModal({
  isOpen,
  requiredField,
  onSubmit,
  onCancel,
  loading = false,
}: ConditionalDataModalProps) {
  // Get field configuration based on field name
  const getFieldConfig = (fieldName: string) => {
    switch (fieldName) {
      case "height":
        return {
          label: "Height",
          unit: "cm",
          values: Array.from({ length: 178 }, (_, i) => i + 73), // 73-250 cm
          defaultValue: 175,
          helpText: "Scroll to select your height",
          detailedExplanation: (
            <>
              To continue with this task, we ask you to enter your height.
              Accurate height data helps us calibrate the Orb's positioning
              relative to each user's stature, ensuring a better user
              experience. For details on how we collect, use, and protect your
              information, please review our{" "}
              <Link to="https://worldcoin.pactsafe.io/rkuawsvk5.html#contract-rjmifoykjl">
                Privacy Notice
              </Link>
              .
            </>
          ),
        };
      case "weight":
        return {
          label: "Weight",
          unit: "kg",
          values: Array.from({ length: 271 }, (_, i) => i + 30), // 30-300 kg
          defaultValue: 70,
          helpText: "Scroll to select your weight",
        };
      case "eye color":
        return {
          label: "Eye Color",
          unit: "",
          values: {
            Brown: "EYE-BROWN",
            Amber: "EYE-AMBER",
            Green: "EYE-GREEN",
            Hazel: "EYE-HAZEL",
            Blue: "EYE-BLUE",
            Grey: "EYE-GREY",
          },
          defaultValue: "EYE-BROWN",
          helpText: "Select your eye color",
          detailedExplanation: (
            <>
              To continue with this task, we ask you to select your eye color.
              This information helps us ensure our biometric models work
              accurately across diverse physical characteristics. For details on
              how we collect, use, and protect your information, please review
              our{" "}
              <Link to="https://worldcoin.pactsafe.io/rkuawsvk5.html#contract-rjmifoykjl">
                Privacy Notice
              </Link>
              .
            </>
          ),
          isStringValue: true,
          useDropdown: true, // Flag to use dropdown instead of picker
        };
      default:
        return {
          label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
          unit: "",
          values: Array.from({ length: 100 }, (_, i) => i + 1), // 1-100 for generic
          defaultValue: 50,
          helpText: `Scroll to select your ${fieldName}`,
        };
    }
  };

  const fieldConfig = getFieldConfig(requiredField);
  const [selectedValue, setSelectedValue] = useState<number | string>(
    fieldConfig.defaultValue
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleFormSubmit = useCallback(() => {
    // For eye color, convert the code back to human-readable value
    let valueToSubmit = selectedValue;

    if (
      requiredField === "eye color" &&
      fieldConfig.useDropdown &&
      typeof fieldConfig.values === "object" &&
      !Array.isArray(fieldConfig.values)
    ) {
      // Find the human-readable key for the selected code value
      const eyeColorMap = fieldConfig.values as Record<string, string>;
      const readableValue = Object.entries(eyeColorMap).find(
        ([_, code]) => code === selectedValue
      )?.[0];

      if (readableValue) {
        valueToSubmit = readableValue;
      }
    }

    const data = { [requiredField]: valueToSubmit };
    onSubmit(data);
  }, [onSubmit, requiredField, selectedValue, fieldConfig]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  // Helper to get display label for dropdown values
  const getDropdownLabel = useCallback(
    (value: string) => {
      if (
        fieldConfig.useDropdown &&
        typeof fieldConfig.values === "object" &&
        !Array.isArray(fieldConfig.values)
      ) {
        const values = fieldConfig.values as Record<string, string>;
        const entry = Object.entries(values).find(([_, val]) => val === value);
        return entry ? entry[0] : value;
      }
      return value;
    },
    [fieldConfig]
  );

  // Set default value when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedValue(fieldConfig.defaultValue);
    }
  }, [isOpen, fieldConfig.defaultValue, requiredField]);

  if (!isOpen) return null;

  const isValid = fieldConfig.isStringValue
    ? selectedValue !== ""
    : typeof selectedValue === "number" && selectedValue > 0;

  return (
    <Modal show={isOpen} onClose={handleCancel}>
      <div
        className="w-full max-w-sm mx-auto max-h-[90vh] overflow-y-auto scrollbar-hide"
        ref={scrollContainerRef}
      >
        <div className="space-y-8 py-6">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-3461fd/10 rounded-full flex items-center justify-center mx-auto">
              <HeightIcon className="w-8 h-8 text-3461fd" />
            </div>
            <div className="space-y-2">
              <h2 className="text-20 font-semibold text-191c20">
                {requiredField === "height"
                  ? "Please Enter Your Height"
                  : "Additional Information Required"}
              </h2>
              <p className="text-858494 text-14 leading-relaxed px-4">
                {fieldConfig.detailedExplanation ||
                  `This task requires your ${fieldConfig.label.toLowerCase()} to proceed.`}
              </p>
            </div>
          </div>

          {/* Picker or Dropdown */}
          <div className="space-y-3">
            <div className="text-center">
              <h3 className="text-16 font-medium text-191c20 mb-4">
                {fieldConfig.label}{" "}
                {fieldConfig.unit && `(${fieldConfig.unit})`}
              </h3>

              {fieldConfig.useDropdown ? (
                <div className="max-w-xs mx-auto">
                  <Field label="">
                    <div
                      onClick={() =>
                        setTimeout(() => {
                          scrollContainerRef.current?.scrollTo({
                            top: scrollContainerRef.current.scrollHeight,
                            behavior: "smooth",
                          });
                        }, 100)
                      }
                    >
                      <Select
                        options={fieldConfig.values as Record<string, string>}
                        value={selectedValue as string}
                        onChange={(value) => setSelectedValue(value as string)}
                        placeholder={`Select ${fieldConfig.label.toLowerCase()}`}
                        renderValue={(item) => getDropdownLabel(item as string)}
                        renderListItem={(item) => item.label}
                        listClassName="max-h-[180px] overflow-y-auto scrollbar-hide"
                      />
                    </div>
                  </Field>
                </div>
              ) : (
                <ScrollablePicker
                  values={fieldConfig.values as (string | number)[]}
                  selectedValue={selectedValue}
                  onValueChange={setSelectedValue}
                  unit={fieldConfig.unit}
                  disabled={loading}
                />
              )}
            </div>

            {fieldConfig.helpText && (
              <p className="text-858494 text-12 text-center">
                {fieldConfig.helpText}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              type="button"
              disabled={!isValid || loading}
              onClick={handleFormSubmit}
              className="w-full py-4 text-16 font-semibold min-h-[48px] rounded-xl shadow-sm"
            >
              {loading
                ? "Saving..."
                : fieldConfig.useDropdown
                ? `Continue with ${getDropdownLabel(selectedValue as string)}`
                : `Continue with ${selectedValue} ${fieldConfig.unit}`}
            </Button>
            <Button
              type="button"
              variant={ButtonVariant.FLAT}
              onClick={handleCancel}
              disabled={loading}
              className="w-full py-3 text-16 text-858494 min-h-[44px] rounded-xl"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
});
